/**
 * ProtectedRoute Component
 * 로그인 및 권한 체크를 통한 라우트 보호
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthorization } from '../hooks/useAuthorization';
import { Permission } from '../../types/auth';
import { auth } from '../../firebase/config';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * 필요한 역할 (하나라도 만족하면 접근 허용)
   */
  requiredRoles?: string[];
  /**
   * 필요한 권한 (하나라도 만족하면 접근 허용)
   */
  requiredPermissions?: Permission[];
  /**
   * 모든 권한이 필요한 경우
   */
  requireAllPermissions?: boolean;
  /**
   * 접근 거부 시 리다이렉트 경로
   */
  redirectTo?: string;
}

/**
 * 인증 및 권한이 필요한 라우트 보호 컴포넌트
 *
 * @example
 * // 로그인만 필요
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * @example
 * // super_admin 또는 hr_admin 역할 필요
 * <ProtectedRoute requiredRoles={['super_admin', 'hr_admin']}>
 *   <HRManagement />
 * </ProtectedRoute>
 *
 * @example
 * // 특정 권한 필요
 * <ProtectedRoute requiredPermissions={['hr.employees.view']}>
 *   <EmployeeList />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  requiredRoles,
  requiredPermissions,
  requireAllPermissions = false,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const {
    user,
    isLoading,
    hasAnyRole,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuthorization();

  // 로딩 중
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: '#666',
        }}
      >
        로딩 중...
      </div>
    );
  }

  // 로그인하지 않음
  // V82.7 Phase 2.7: Firebase auth.currentUser 를 fallback 으로 사용.
  //   useAuthorization → useCurrentUser 가 onAuthStateChanged 콜백 안에서 비동기 Firestore
  //   조회를 수행하기 때문에, MFA 직후 짧은 시간 동안 user=null + isLoading=false 인
  //   "공백 윈도우" 가 존재할 수 있음. 이 윈도우에서 Firebase 자체는 이미 로그인 완료
  //   상태이므로 currentUser 가 truthy → 통과시켜야 함. 그래야 자식 ExternalAccessMfaGuard
  //   가 정상 평가 가능.
  const fbUser = auth?.currentUser;
  if (!user && !fbUser) {
    console.log('[ProtectedRoute redirect /login]', {
      reason: '!user && !fbUser',
      isLoading,
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      redirectTo,
    });
    return <Navigate to={redirectTo} replace />;
  }
  if (!user && fbUser) {
    // 안전망 통과 — useCurrentUser 가 채워질 때까지 children 진입 허용
    console.log('[ProtectedRoute fallback]', {
      reason: 'user pending but fbUser exists',
      fbUid: fbUser.uid,
    });
  }

  // 역할 체크
  if (requiredRoles && requiredRoles.length > 0) {
    if (!hasAnyRole(requiredRoles)) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            gap: '16px',
          }}
        >
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지에 접근할 수 있는 권한이 없습니다.</p>
          <p style={{ color: '#999' }}>
            필요한 역할: {requiredRoles.join(', ')}
          </p>
        </div>
      );
    }
  }

  // 권한 체크
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasPermission = requireAllPermissions
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);

    if (!hasPermission) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            gap: '16px',
          }}
        >
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지에 접근할 수 있는 권한이 없습니다.</p>
          <p style={{ color: '#999' }}>
            필요한 권한: {requiredPermissions.join(', ')}
          </p>
        </div>
      );
    }
  }

  // 모든 검사 통과
  return <>{children}</>;
}
